// Cálculos puros da calculadora "Minha Cron Vale a Pena?". Este módulo é
// importado pelo servidor e pelo bundle do navegador (client/features/melhor-cron),
// por isso não pode depender de Node nem do DOM. Nada é arredondado aqui:
// o arredondamento acontece somente na apresentação.

export const ITEM_DESTINATIONS = Object.freeze(['extract', 'sell', 'keep', 'other']);

export const DEFAULT_BAND_THRESHOLDS = Object.freeze({ excellent: 75, normal: 35, low: 10 });

function finite(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

export function effectivePearls(originalPrice, discountPercent) {
  return finite(originalPrice) * (1 - finite(discountPercent) / 100);
}

export function evaluatePurchase({ originalPrice, discountPercent, items = [] }) {
  const paid = effectivePearls(originalPrice, discountPercent);
  let totalCrons = 0;
  let totalSilver = 0;
  let totalItems = 0;
  for (const item of items) {
    const quantity = finite(item?.quantity);
    totalItems += quantity;
    if (item?.destination === 'extract') totalCrons += quantity * finite(item.cronPerUnit);
    if (item?.destination === 'sell') totalSilver += quantity * finite(item.silverPerUnit);
  }
  return {
    effectivePearls: paid,
    savedPearls: finite(originalPrice) - paid,
    totalCrons,
    totalSilver,
    totalItems,
    cronPerPearl: paid > 0 ? totalCrons / paid : 0,
    pearlsPer1000Crons: totalCrons > 0 ? (paid / totalCrons) * 1000 : null
  };
}

// Percentual dos registros históricos com Cron/Pérola estritamente menor.
export function percentileOf(sortedValues, value) {
  if (!sortedValues.length) return null;
  let low = 0;
  let high = sortedValues.length;
  while (low < high) {
    const middle = (low + high) >> 1;
    if (sortedValues[middle] < value) low = middle + 1; else high = middle;
  }
  return (low / sortedValues.length) * 100;
}

export function average(values) {
  return values.length ? values.reduce((total, value) => total + value, 0) / values.length : null;
}

export function efficiencyBand(percentile, thresholds = DEFAULT_BAND_THRESHOLDS) {
  if (percentile === null || percentile === undefined) return 'unknown';
  if (percentile >= thresholds.excellent) return 'excellent';
  if (percentile >= thresholds.normal) return 'normal';
  if (percentile >= thresholds.low) return 'low';
  return 'terrible';
}

// Posição de um Cron/Pérola frente à distribuição histórica (valores ordenados).
export function historicalPosition(value, sortedValues, { minSample = 5, thresholds = DEFAULT_BAND_THRESHOLDS } = {}) {
  const sample = sortedValues.length;
  if (!(value > 0) || sample < minSample) return { sample, enough: false, percentile: null, average: average(sortedValues), vsAverage: null, band: 'unknown' };
  const mean = average(sortedValues);
  const percentile = percentileOf(sortedValues, value);
  return { sample, enough: true, percentile, average: mean, vsAverage: mean > 0 ? (value / mean - 1) * 100 : null, band: efficiencyBand(percentile, thresholds) };
}

// Quanto `candidate` entrega a mais (ou a menos) de Crons por Pérola que `base`, em %.
export function relativeEfficiency(candidate, base) {
  return base > 0 ? (candidate / base - 1) * 100 : null;
}

export function normalizePackageName(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

// Chave de equivalência: mesmo nome, preço cheio, região e conteúdo. O desconto
// fica de fora porque varia por cupom; a proximidade de datas é tratada ao agrupar.
export function purchaseGroupKey({ packageName, originalPrice, region, items = [] }) {
  const content = items
    .map((item) => `${item.itemId || normalizePackageName(item.name)}x${finite(item.quantity)}`)
    .sort()
    .join(',');
  return [normalizePackageName(packageName), finite(originalPrice), region || '', content].join('|');
}
